import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import type { LaborEmployee, LaborRecord } from '../types/timelabor';
import type { QueryOptions } from '../api/types';

export type { LaborEmployee, LaborRecord };

const FE = FIELDS.laborEmployees;
const FL = FIELDS.forceAccountLabor;

function mapEmployee(r: Record<string, unknown>): LaborEmployee {
  const id = r.record_id as number;
  const totalFringePct = Number(r[FE.totalFringePct]) || 0;
  const regularRate = Number(r[FE.regularRate]) || 0;
  const fringeHourlyRate = regularRate * totalFringePct / 100;
  return {
    id, Rid: id,
    employeeName: String(r[FE.employeeName] || ''),
    employeeId: String(r[FE.employeeId] || ''),
    jobTitle: String(r[FE.jobTitle] || ''),
    department: String(r[FE.department] || ''),
    employmentType: String(r[FE.employmentType] || ''),
    flsaStatus: String(r[FE.flsaStatus] || ''),
    budgetStatus: String(r[FE.budgetStatus] || ''),
    regularRate,
    overtimeRate: Number(r[FE.overtimeRate]) || 0,
    totalFringePct,
    totalHourlyRate: Number(r[FE.totalHourlyRate]) || 0,
    rosterEmail: String(r[FE.rosterEmail] || ''),
    organizationId: r[FE.organizationId] != null ? (r[FE.organizationId] as number) : null,
    normalWeeklyHours: Number(r[FE.normalWeeklyHours]) || 40,
    hireDate: String(r[FE.hireDate] || ''),
    defaultAccountCode: String(r[FE.defaultAccountCode] || ''),
    defaultProgramCode: String(r[FE.defaultProgramCode] || ''),
    status: String(r[FE.status] || 'active'),
    notes: String(r[FE.notes] || ''),
    fringeFicaPct: Number(r[FE.fringeFicaPct]) || 0,
    fringeRetirementPct: Number(r[FE.fringeRetirementPct]) || 0,
    fringeHealthPct: Number(r[FE.fringeHealthPct]) || 0,
    fringeWorkersCompPct: Number(r[FE.fringeWorkersCompPct]) || 0,
    fringeUnemploymentPct: Number(r[FE.fringeUnemploymentPct]) || 0,
    fringeOtherPct: Number(r[FE.fringeOtherPct]) || 0,
    fringeHourlyRate,
  };
}


function mapRecord(r: Record<string, unknown>): LaborRecord {
  const id = r.record_id as number;
  const status = String(r[FL.status] || 'draft');
  return {
    id, Rid: id,
    incidentId: r[FL.incidentId] != null ? r[FL.incidentId] as number : null,
    projectId: r[FL.projectId] != null ? r[FL.projectId] as number : null,
    organizationId: r[FL.organizationId] != null ? r[FL.organizationId] as number : null,
    employeeName: String(r[FL.employeeName] || ''),
    employeeId: String(r[FL.employeeId] || ''),
    employeeTitle: String(r[FL.employeeTitle] || ''),
    workDate: String(r[FL.workDate] || ''),
    hoursRegular: Number(r[FL.hoursRegular]) || 0,
    hoursOvertime: Number(r[FL.hoursOvertime]) || 0,
    rateRegular: Number(r[FL.rateRegular]) || 0,
    rateOvertime: Number(r[FL.rateOvertime]) || 0,
    laborCost: Number(r[FL.laborCost]) || 0,
    fringeCost: Number(r[FL.fringeCost]) || 0,
    totalCost: Number(r[FL.totalCost]) || 0,
    fringeBenefitsPct: Number(r[FL.fringeBenefitsPct]) || 0,
    fringeFica: Number(r[FL.fringeFica]) || 0,
    fringeRetirement: Number(r[FL.fringeRetirement]) || 0,
    fringeHealth: Number(r[FL.fringeHealth]) || 0,
    fringeWorkersComp: Number(r[FL.fringeWorkersComp]) || 0,
    fringeUnemployment: Number(r[FL.fringeUnemployment]) || 0,
    fringeOther: Number(r[FL.fringeOther]) || 0,
    activityDescription: String(r[FL.activityDescription] || ''),
    category: String(r[FL.category] || ''),
    status,
    timesheetOnFile: status, // V1 compat alias — same as status
    approvedRegHours: Number(r[FL.approvedRegHours]) || 0,
    approvedOTHours: Number(r[FL.approvedOTHours]) || 0,
    approvedCost: Number(r[FL.approvedCost]) || 0,
    reviewerNotes: String(r[FL.reviewerNotes] || ''),
    accountCode: String(r[FL.accountCode] || ''),
    programCode: String(r[FL.programCode] || ''),
    shiftPremium: String(r[FL.shiftPremium] || ''),
    notes: String(r[FL.notes] || ''),
    timecardBatch: String(r[FL.timecardBatch] || ''),
  };
}

/**
 * Convert friendly-named employee data to Ignatius field keys.
 * Accepts both friendly names and already-keyed data (keys are skipped if not recognized).
 */
function employeeToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  // Accept data keyed by either friendly name OR already field-keyed (pass-through unknown keys)
  if (data.employeeName !== undefined) p[FE.employeeName] = data.employeeName;
  if (data.employeeId !== undefined) p[FE.employeeId] = data.employeeId;
  if (data.jobTitle !== undefined) p[FE.jobTitle] = data.jobTitle;
  if (data.department !== undefined) p[FE.department] = data.department;
  if (data.employmentType !== undefined) p[FE.employmentType] = data.employmentType;
  if (data.flsaStatus !== undefined) p[FE.flsaStatus] = data.flsaStatus;
  if (data.budgetStatus !== undefined) p[FE.budgetStatus] = data.budgetStatus;
  if (data.regularRate !== undefined) p[FE.regularRate] = data.regularRate;
  if (data.overtimeRate !== undefined) p[FE.overtimeRate] = data.overtimeRate;
  if (data.totalFringePct !== undefined) p[FE.totalFringePct] = data.totalFringePct;
  if (data.totalHourlyRate !== undefined) p[FE.totalHourlyRate] = data.totalHourlyRate;
  if (data.rosterEmail !== undefined) p[FE.rosterEmail] = data.rosterEmail;
  if (data.organizationId !== undefined) p[FE.organizationId] = data.organizationId;
  if (data.normalWeeklyHours !== undefined) p[FE.normalWeeklyHours] = data.normalWeeklyHours;
  if (data.hireDate !== undefined) p[FE.hireDate] = data.hireDate;
  if (data.defaultAccountCode !== undefined) p[FE.defaultAccountCode] = data.defaultAccountCode;
  if (data.defaultProgramCode !== undefined) p[FE.defaultProgramCode] = data.defaultProgramCode;
  if (data.status !== undefined) p[FE.status] = data.status;
  if (data.notes !== undefined) p[FE.notes] = data.notes;
  if (data.fringeFicaPct !== undefined) p[FE.fringeFicaPct] = Number(data.fringeFicaPct) || 0;
  if (data.fringeRetirementPct !== undefined) p[FE.fringeRetirementPct] = Number(data.fringeRetirementPct) || 0;
  if (data.fringeHealthPct !== undefined) p[FE.fringeHealthPct] = Number(data.fringeHealthPct) || 0;
  if (data.fringeWorkersCompPct !== undefined) p[FE.fringeWorkersCompPct] = Number(data.fringeWorkersCompPct) || 0;
  if (data.fringeUnemploymentPct !== undefined) p[FE.fringeUnemploymentPct] = Number(data.fringeUnemploymentPct) || 0;
  if (data.fringeOtherPct !== undefined) p[FE.fringeOtherPct] = Number(data.fringeOtherPct) || 0;
  return p;
}

/**
 * Convert friendly-named labor record data to Ignatius field keys.
 * Also handles V1 compat field names (timesheetOnFile → status).
 */
function laborRecordToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId !== undefined) p[FL.incidentId] = data.incidentId;
  if (data.projectId !== undefined) p[FL.projectId] = data.projectId;
  if (data.organizationId !== undefined) p[FL.organizationId] = data.organizationId;
  if (data.employeeName !== undefined) p[FL.employeeName] = data.employeeName;
  if (data.employeeId !== undefined) p[FL.employeeId] = data.employeeId;
  if (data.employeeTitle !== undefined) p[FL.employeeTitle] = data.employeeTitle;
  if (data.workDate !== undefined) p[FL.workDate] = data.workDate;
  if (data.hoursRegular !== undefined) p[FL.hoursRegular] = data.hoursRegular;
  if (data.hoursOvertime !== undefined) p[FL.hoursOvertime] = data.hoursOvertime;
  if (data.rateRegular !== undefined) p[FL.rateRegular] = data.rateRegular;
  if (data.rateOvertime !== undefined) p[FL.rateOvertime] = data.rateOvertime;
  if (data.laborCost !== undefined) p[FL.laborCost] = data.laborCost;
  if (data.fringeCost !== undefined) p[FL.fringeCost] = data.fringeCost;
  if (data.totalCost !== undefined) p[FL.totalCost] = data.totalCost;
  if (data.fringeBenefitsPct !== undefined) p[FL.fringeBenefitsPct] = data.fringeBenefitsPct;
  if (data.fringeFica !== undefined) p[FL.fringeFica] = data.fringeFica;
  if (data.fringeRetirement !== undefined) p[FL.fringeRetirement] = data.fringeRetirement;
  if (data.fringeHealth !== undefined) p[FL.fringeHealth] = data.fringeHealth;
  if (data.fringeWorkersComp !== undefined) p[FL.fringeWorkersComp] = data.fringeWorkersComp;
  if (data.fringeUnemployment !== undefined) p[FL.fringeUnemployment] = data.fringeUnemployment;
  if (data.fringeOther !== undefined) p[FL.fringeOther] = data.fringeOther;
  if (data.activityDescription !== undefined) p[FL.activityDescription] = data.activityDescription;
  if (data.category !== undefined) p[FL.category] = data.category;
  // timesheetOnFile is the V1 name for status
  if (data.timesheetOnFile !== undefined) p[FL.status] = data.timesheetOnFile;
  if (data.status !== undefined) p[FL.status] = data.status;
  if (data.approvedRegHours !== undefined) p[FL.approvedRegHours] = data.approvedRegHours;
  if (data.approvedOTHours !== undefined) p[FL.approvedOTHours] = data.approvedOTHours;
  if (data.approvedCost !== undefined) p[FL.approvedCost] = data.approvedCost;
  if (data.reviewerNotes !== undefined) p[FL.reviewerNotes] = data.reviewerNotes;
  if (data.accountCode !== undefined) p[FL.accountCode] = data.accountCode;
  if (data.programCode !== undefined) p[FL.programCode] = data.programCode;
  if (data.shiftPremium !== undefined) p[FL.shiftPremium] = data.shiftPremium;
  if (data.notes !== undefined) p[FL.notes] = data.notes;
  if (data.timecardBatch !== undefined) p[FL.timecardBatch] = data.timecardBatch;
  return p;
}

interface TimeLaborState {
  employees: LaborEmployee[]; employeesLoading: boolean;
  records: LaborRecord[]; recordsTotal: number; recordsLoading: boolean; recordsPage: number;
  fetchEmployees: () => Promise<void>; fetchRecords: (opts?: Partial<QueryOptions>) => Promise<void>;
  setRecordsPage: (p: number) => void;
  createEmployee: (data: Record<string, unknown>) => Promise<number | null>;
  updateEmployee: (id: number, data: Record<string, unknown>) => Promise<void>;
  createRecord: (data: Record<string, unknown>) => Promise<number | null>;
  updateRecord: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteRecord: (id: number) => Promise<void>;
  approveRecord: (id: number, approved: Record<string, unknown>) => Promise<void>;

  // V1 compat aliases
  laborRecords: LaborRecord[];
  loadingRecords: boolean;
  loadingEmployees: boolean;
  fetchLaborRecords: (opts?: Partial<QueryOptions>) => Promise<void>;
  createLaborRecord: (data: Record<string, unknown>) => Promise<number | null>;
  updateLaborRecord: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteLaborRecord: (id: number) => Promise<void>;
  deleteEmployee: (id: number) => Promise<void>;
}

export const useTimeLaborStore = create<TimeLaborState>((set, get) => ({
  employees: [], employeesLoading: false,
  records: [], recordsTotal: 0, recordsLoading: false, recordsPage: 1,

  fetchEmployees: async () => {
    set({ employeesLoading: true, loadingEmployees: true });
    try { const res = await fetchRecords<Record<string, unknown>>(TABLES.laborEmployees, { limit: 500 }); set({ employees: res.records.map(mapEmployee) }); }
    catch (e) { console.error('[TimeLab] fetchEmployees', e); }
    set({ employeesLoading: false, loadingEmployees: false });
  },

  fetchRecords: async (opts) => {
    // Only show loading spinner on first load — background polls should not blink the UI
    const isFirstLoad = get().records.length === 0;
    if (isFirstLoad) set({ recordsLoading: true, loadingRecords: true });
    try {
      const { recordsPage } = get();
      const res = await queryRecords<Record<string, unknown>>(TABLES.forceAccountLabor, {
        limit: 500, offset: (recordsPage - 1) * 500, sort: { field: 'created_at', direction: 'desc' }, ...opts,
      });
      const mapped = res.records.map(mapRecord);
      set({ records: mapped, recordsTotal: res.total, laborRecords: mapped });
    } catch (e) { console.error('[TimeLab] fetchRecords', e); }
    set({ recordsLoading: false, loadingRecords: false });
  },

  setRecordsPage: (p) => { set({ recordsPage: p }); get().fetchRecords(); },
  createEmployee: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.laborEmployees, employeeToPayload(data)); await get().fetchEmployees(); return (r as any).record_id; } catch (e) { console.error('[TimeLab]', e); return null; } },
  updateEmployee: async (id, data) => { try { await updateRecord(TABLES.laborEmployees, id, employeeToPayload(data)); await get().fetchEmployees(); } catch (e) { console.error('[TimeLab]', e); } },
  createRecord: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.forceAccountLabor, laborRecordToPayload(data)); await get().fetchRecords(); return (r as any).record_id; } catch (e) { console.error('[TimeLab]', e); return null; } },
  updateRecord: async (id, data) => { try { await updateRecord(TABLES.forceAccountLabor, id, laborRecordToPayload(data)); await get().fetchRecords(); } catch (e) { console.error('[TimeLab]', e); } },
  deleteRecord: async (id) => { try { await deleteRecord(TABLES.forceAccountLabor, id); await get().fetchRecords(); } catch (e) { console.error('[TimeLab]', e); } },
  approveRecord: async (id, approved) => { try { await updateRecord(TABLES.forceAccountLabor, id, laborRecordToPayload({ ...approved, status: 'approved' })); await get().fetchRecords(); } catch (e) { console.error('[TimeLab]', e); } },

  // V1 compat aliases — initial values; real data accessed via store.records etc.
  laborRecords: [] as LaborRecord[],
  loadingRecords: false,
  loadingEmployees: false,
  fetchLaborRecords: async (opts?: Partial<QueryOptions>) => { await get().fetchRecords(opts); },
  createLaborRecord: async (data: Record<string, unknown>) => { return get().createRecord(data); },
  updateLaborRecord: async (id: number, data: Record<string, unknown>) => { return get().updateRecord(id, data); },
  deleteLaborRecord: async (id: number) => { return get().deleteRecord(id); },
  deleteEmployee: async (id: number) => { try { await deleteRecord(TABLES.laborEmployees, id); await get().fetchEmployees(); } catch (e) { console.error('[TimeLab]', e); } },
}));
